/**
 * FBTR roll picker: choose one or more rolls (variants) from a dropdown and add them to the cart together.
 *
 * Plain JS (no jQuery). The Add to cart button is the theme's own `.product-form__cart-submit`, so the theme's
 * ajax add, cart popup and cart count keep working. Just before the theme's handler runs we write the ticked rolls
 * into the form as `items[n][id]` / `items[n][quantity]`, and it posts them to /cart/add.js in one request.
 */
class FbtrRollPicker extends HTMLElement {
  #bound = false;

  get list() {
    return this.querySelector('[data-list]');
  }

  connectedCallback() {
    if (this.#bound) return;
    this.#bound = true;

    this.addEventListener('change', () => this.#update());
    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const swatch = target?.closest('.fbtr-picker__swatch');
      if (swatch instanceof HTMLButtonElement) this.#setColour(swatch, true);
      else if (target?.closest('[data-select-all]')) this.#setAll(true);
      else if (target?.closest('[data-clear]')) this.#setAll(false);
      else if (target?.closest('[data-buy]')) this.#buyNow();
    });
    // Capture phase: runs before the theme's own click handler on the Add to cart button
    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('[data-add]')) this.#prepareThemeForm(event);
    }, true);

    // The theme redraws #cart-info after every add: use that to mark rolls that are now in the cart
    const cartInfo = document.getElementById('cart-info');
    if (cartInfo) new MutationObserver(() => this.#markInCart()).observe(cartInfo, { childList: true });

    const current = this.querySelector('.fbtr-picker__swatch[aria-pressed="true"]');
    if (current instanceof HTMLButtonElement) this.#setColour(current, false);
    this.#sortRows();
    this.#markInCart();
    this.#update();
  }

  get #checked() {
    return this.list ? [...this.list.querySelectorAll('input[type="checkbox"]:checked')] : [];
  }

  /** Longest roll first */
  #sortRows() {
    if (!this.list) return;
    const length = (row) => parseFloat(row.querySelector('input')?.dataset.length || '0');
    [...this.list.querySelectorAll('.fbtr-picker__row')].sort((a, b) => length(b) - length(a)).forEach((row) => this.list.append(row));
  }

  /** Each roll is one item in stock, so a roll already in the cart can't be added again */
  async #markInCart() {
    if (!this.list) return;
    let ids;
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      ids = new Set(cart.items.map((item) => item.variant_id));
    } catch {
      return;
    }
    this.list.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      const inCart = ids.has(Number(input.value));
      if (inCart) input.checked = false;
      input.disabled = inCart;
      input.closest('.fbtr-picker__row')?.classList.toggle('fbtr-picker__row--in-cart', inCart);
    });
    this.#update();
  }

  /** @param {boolean} value */
  #setAll(value) {
    this.list?.querySelectorAll('.fbtr-picker__row:not([hidden]) input[type="checkbox"]:not(:disabled)').forEach((input) => (input.checked = value));
    this.#update();
  }

  /**
   * Basics: show only the rolls of one colour and move the photo gallery to that colour.
   * @param {HTMLButtonElement} swatch
   * @param {boolean} moveGallery
   */
  #setColour(swatch, moveGallery) {
    const colour = swatch.dataset.colour;
    this.querySelectorAll('.fbtr-picker__swatch').forEach((el) => el.setAttribute('aria-pressed', String(el === swatch)));
    const name = this.querySelector('[data-colour-name]');
    if (name) name.textContent = colour || '';
    let shown = 0;
    this.list?.querySelectorAll('.fbtr-picker__row').forEach((row) => {
      const match = /** @type {HTMLElement} */ (row).dataset.colour === colour;
      row.toggleAttribute('hidden', !match);
      const input = row.querySelector('input');
      if (!match && input) input.checked = false;
      if (match) shown++;
    });
    const count = this.querySelector('[data-count]');
    if (count) count.textContent = `${shown} ${shown === 1 ? 'roll' : 'rolls'}`;
    // Quick add pop-up: one photo, which follows the colour
    const popupImage = this.closest('.fbtr-qv__grid')?.querySelector('.fbtr-qv__media img');
    if (moveGallery && popupImage instanceof HTMLImageElement && swatch.dataset.img) {
      popupImage.removeAttribute('srcset');
      popupImage.src = swatch.dataset.img;
    }
    if (moveGallery && swatch.dataset.pos) {
      try {
        // The theme's gallery is a slick carousel
        /** @type {any} */ (window).jQuery?.('.FeaturedImage_slick').slick('slickGoTo', Number(swatch.dataset.pos));
      } catch {
        /* gallery not ready: leave it */
      }
    }
    this.#update();
  }

  /**
   * Formats cents with the shop's money format (e.g. "R {{amount}}"), falling back to Intl.
   * @param {number} cents
   */
  #money(cents) {
    const format = this.dataset.moneyFormat;
    const group = (value, decimals, thousands = ',', decimal = '.') => {
      const [whole, fraction] = (value / 100).toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    const match = format?.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!format || !match) {
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.dataset.currency || 'ZAR' }).format(cents / 100);
      } catch {
        return (cents / 100).toFixed(2);
      }
    }
    const amount = {
      amount: group(cents, 2),
      amount_no_decimals: group(cents, 0),
      amount_with_comma_separator: group(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: group(cents, 0, '.'),
      amount_with_space_separator: group(cents, 2, ' ', ','),
      amount_with_apostrophe_separator: group(cents, 2, "'"),
    }[match[1]] ?? group(cents, 2);
    return format.replace(match[0], amount);
  }

  #update() {
    const checked = this.#checked;
    const count = checked.length;
    const cents = checked.reduce((sum, input) => sum + Number(input.dataset.price || 0), 0);
    const wasCents = checked.reduce((sum, input) => {
      const price = Number(input.dataset.price || 0);
      const compare = Number(input.dataset.compare || 0);
      return sum + (compare > price ? compare : price);
    }, 0);
    const metres = checked.reduce((sum, input) => sum + parseFloat(input.dataset.length || '0'), 0);

    const summary = this.querySelector('[data-summary]');
    if (summary) summary.textContent = count ? `${count} selected` : this.dataset.labelChoose || 'Select rolls';

    const selectable = this.list ? this.list.querySelectorAll('.fbtr-picker__row:not([hidden]) input[type="checkbox"]:not(:disabled)').length : 0;

    // Roll price is only shown once rolls are chosen
    const total = this.querySelector('[data-total]');
    if (total) {
      total.replaceChildren();
      if (!count && !selectable) {
        total.append(this.#span('fbtr-picker__meta', 'All available rolls are in your cart.'));
      } else if (count) {
        const meta = [`${count} ${count === 1 ? 'roll' : 'rolls'}`];
        if (metres) meta.push(`${Math.round(metres * 10) / 10} m`);
        total.append(this.#span('fbtr-picker__meta', meta.join(' · ')));
        const price = this.#span('fbtr-picker__price', '');
        // sale price on top, the normal price struck through underneath
        price.append(this.#span(wasCents > cents ? 'product-price__sale' : '', this.#money(cents)));
        if (wasCents > cents) price.append(this.#el('s', this.#money(wasCents)));
        if (this.dataset.vat) {
          const vat = this.#el('small', this.dataset.vat);
          vat.className = 'fbtr-vat';
          price.append(' ', vat);
        }
        total.append(price);
      }
    }

    this.#notes(checked, metres);

    const label = this.querySelector('[data-add-text]');
    if (label) label.textContent = count > 1 ? `Add ${count} rolls to cart` : 'Add to cart';
    const add = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-add]'));
    if (add) add.disabled = count === 0;
    const buy = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-buy]'));
    if (buy) buy.disabled = count === 0;
  }

  /**
   * Dye-ID match, and a rough "what will these rolls make" line.
   * @param {HTMLInputElement[]} checked
   * @param {number} metres
   */
  #notes(checked, metres) {
    const lotNote = /** @type {HTMLElement | null} */ (this.querySelector('[data-lot-note]'));
    if (lotNote) {
      const lots = new Set(checked.map((input) => input.dataset.lot).filter(Boolean));
      lotNote.hidden = checked.length < 2 || lots.size === 0;
      lotNote.toggleAttribute('data-same', lots.size === 1);
      const strong = document.createElement('b');
      if (lots.size === 1) {
        strong.textContent = 'Same dye ID';
        lotNote.replaceChildren(strong, ` (${[...lots][0]}): the colours will match.`);
      } else {
        strong.textContent = `${lots.size} different dye IDs`;
        lotNote.replaceChildren(strong, ': shades may vary slightly between rolls.');
      }
    }

    const calc = /** @type {HTMLElement | null} */ (this.querySelector('[data-calc]'));
    if (calc) {
      calc.hidden = !metres;
      if (metres) {
        // Metres per item at 140 cm wide; narrower fabric needs a little more
        const factor = 140 / (parseFloat(this.dataset.width || '140') || 140);
        const uses = [['dress', 'dresses', 2.5], ['shirt', 'shirts', 1.8], ['cushion cover', 'cushion covers', 0.5], ['single quilt', 'single quilts', 5]];
        const parts = uses.map(([one, many, per]) => {
          const n = Math.floor(metres / (Number(per) * factor));
          return `<b>${n.toLocaleString()}</b> ${n === 1 ? one : many}`;
        });
        calc.innerHTML = `Enough for about ${parts.slice(0, 3).join(', ')} or ${parts[3]}.`;
      }
    }
  }

  /** The ticked rolls as cart line items, with the order note (if any) on each */
  #lineItems() {
    const note = /** @type {HTMLTextAreaElement | null} */ (document.getElementById('notes'));
    const notes = note?.value.trim();
    return this.#checked.map((input) => {
      const item = { id: Number(input.value), quantity: 1 };
      return notes ? { ...item, properties: { Notes: notes } } : item;
    });
  }

  /**
   * Runs just before the theme's own click handler: it posts the form with jQuery, so give it the ticked rolls.
   * @param {Event} event
   */
  #prepareThemeForm(event) {
    const items = this.#lineItems();
    if (!items.length) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    const holder = this.querySelector('[data-items]');
    if (!holder) return;
    holder.replaceChildren();
    items.forEach((item, index) => {
      this.#hidden(holder, `items[${index}][id]`, String(item.id));
      this.#hidden(holder, `items[${index}][quantity]`, '1');
      if (item.properties) this.#hidden(holder, `items[${index}][properties][Notes]`, item.properties.Notes);
    });
    // The order note belongs on the line items above, so keep the loose field out of this request
    const note = /** @type {HTMLTextAreaElement | null} */ (document.getElementById('notes'));
    if (note) {
      note.disabled = true;
      setTimeout(() => (note.disabled = false), 0);
    }
  }

  async #buyNow() {
    const items = this.#lineItems();
    if (!items.length) return;
    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-error]'));
    if (error) error.hidden = true;
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
      window.location.href = '/checkout';
    } catch (err) {
      if (error) {
        error.textContent = err instanceof Error ? err.message : String(err);
        error.hidden = false;
      }
    }
  }

  /**
   * @param {Element} parent
   * @param {string} name
   * @param {string} value
   */
  #hidden(parent, name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    parent.append(input);
  }

  /**
   * @param {string} className
   * @param {string} text
   */
  #span(className, text) {
    const span = this.#el('span', text);
    if (className) span.className = className;
    return span;
  }

  /**
   * @param {string} tag
   * @param {string} text
   */
  #el(tag, text) {
    const el = document.createElement(tag);
    el.textContent = text;
    return el;
  }
}

if (!customElements.get('fbtr-roll-picker')) customElements.define('fbtr-roll-picker', FbtrRollPicker);
